import type { MaintenanceScheduleEntry, ServiceStatus } from '@cartrack/shared';

export interface ScheduleSummary {
  overdue: number;
  dueSoon: number;
  upcoming: number;
  ok: number;
}

export function summarizeSchedule(entries: MaintenanceScheduleEntry[]): ScheduleSummary {
  const summary: ScheduleSummary = { overdue: 0, dueSoon: 0, upcoming: 0, ok: 0 };
  for (const e of entries) {
    if (e.status === 'OVERDUE') summary.overdue++;
    else if (e.status === 'DUE_SOON') summary.dueSoon++;
    else if (e.status === 'UPCOMING') summary.upcoming++;
    else summary.ok++;
  }
  return summary;
}

const PRIORITY: Record<ServiceStatus, number> = {
  OVERDUE: 0,
  DUE_SOON: 1,
  UPCOMING: 2,
  OK: 3,
};

function urgencyMetric(e: MaintenanceScheduleEntry): number {
  if (e.milesUntilDue !== null) return e.milesUntilDue;
  if (e.daysUntilDue !== null) return e.daysUntilDue;
  return Number.POSITIVE_INFINITY;
}

export function nextUpcoming(
  entries: MaintenanceScheduleEntry[],
): MaintenanceScheduleEntry | null {
  const candidates = entries.filter((e) => e.status !== 'OK');
  if (candidates.length === 0) return null;
  return [...candidates].sort((a, b) => {
    const priorityDiff = PRIORITY[a.status] - PRIORITY[b.status];
    if (priorityDiff !== 0) return priorityDiff;
    return urgencyMetric(a) - urgencyMetric(b);
  })[0]!;
}

export function statusLabel(status: ServiceStatus): string {
  switch (status) {
    case 'OVERDUE':
      return 'Overdue';
    case 'DUE_SOON':
      return 'Due soon';
    case 'UPCOMING':
      return 'Upcoming';
    case 'OK':
      return 'OK';
  }
}

export function statusColor(status: ServiceStatus): string {
  switch (status) {
    case 'OVERDUE':
      return '$red10';
    case 'DUE_SOON':
      return '$orange10';
    case 'UPCOMING':
      // Distinct non-brand blue: $blue10 rendered ~= Signal Blue (#2997ff) in the
      // dark theme (~15 RGB distance), competing with the brand accent. $blue8 is a
      // deeper blue (dark rgb(9,84,165) ~117 away; light rgb(94,176,239) ~61 away)
      // that stays in the blue family but is clearly separated from the accent in
      // both themes. See test/specs/color-system-fidelity.md.
      return '$blue8';
    case 'OK':
      return '$green10';
  }
}

export function sortSchedule(
  entries: MaintenanceScheduleEntry[],
): MaintenanceScheduleEntry[] {
  return [...entries].sort((a, b) => {
    const priorityDiff = PRIORITY[a.status] - PRIORITY[b.status];
    if (priorityDiff !== 0) return priorityDiff;
    return urgencyMetric(a) - urgencyMetric(b);
  });
}
